import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { map } from 'rxjs';
import { CachedContent } from '../../../interfaces/cached-content';
import { Seo } from '../../../interfaces/seo';
import { SeoContentService } from '../../../services/seo-content/seo-content.service';
import { CacheService } from '../../../ssr-services/cache/cache.service';
import { CACHE_WARMUP_SERVICE_TOKEN } from '../../../tokens/server-tokens';
import { AffiliateProgramSeoStrategy } from './seo-handling-strategies/affiliate-program.seo-strategy';
import { CountrySeoStrategy } from './seo-handling-strategies/country.seo-strategy';
import { DefaultSeoStrategy } from './seo-handling-strategies/default.seo-strategy';
import { DestinationSeoStrategy } from './seo-handling-strategies/destination.seo-strategy';
import { FaqSeoStrategy } from './seo-handling-strategies/faq.seo-strategy';
import { GlobalRegionSeoStrategy } from './seo-handling-strategies/global-region.seo-strategy';
import { HelpSeoStrategy } from './seo-handling-strategies/help.seo-strategy';
import { HomeSeoStrategy } from './seo-handling-strategies/home.seo-strategy';
import { HowItWorksSeoStrategy } from './seo-handling-strategies/how-it-works.seo-strategy';
import { InstallationSeoStrategy } from './seo-handling-strategies/installation.seo-strategy';
import { PrivacyPolicySeoStrategy } from './seo-handling-strategies/privacy-policy.seo-strategy';
import { RegionSeoStrategy } from './seo-handling-strategies/region.seo-strategy';
import { RewardPointsSeoStrategy } from './seo-handling-strategies/reward-points.seo-strategy';
import { SeoStrategyFn } from './seo-handling-strategies/seo-strategy.type';
import { SupportedDevicesSeoStrategy } from './seo-handling-strategies/supported-devices.seo-strategy';
import { TermsAndConditionsSeoStrategy } from './seo-handling-strategies/terms-and-conditions.seo-strategy';

/**
 * Route constants for SEO page mapping
 */
const ROUTES = {
  HOME: '/',
  FAQ: '/faq',
  INSTALLATION: '/installation',
  CONTACT: '/contact-us',
  DESTINATION: '/destination',
  BUY_PLAN_DESTINATION: '/buy-plan/destination',
  SUPPORTED_DEVICES: '/esim-compatible-devices',
  SUPPORTED_DEVICES_ALT: '/supported-devices',
  HOW_IT_WORKS: '/how-it-works',
  TERMS: '/terms-and-conditions',
  PRIVACY: '/privacy-policy',
  AFFILIATE: '/affiliate-program',
  REWARDS: '/reward-points',
} as const;

/**
 * Regex patterns for dynamic routes
 */
const ROUTE_PATTERNS = {
  COUNTRY: /^\/buy-plan\/plans\/country\/([a-f0-9]+)(?:\/.*)?$/,
  REGION: /^\/buy-plan\/plans\/region\/([a-f0-9]+)(?:\/.*)?$/,
  GLOBAL: /^\/buy-plan\/plans\/global\/([a-f0-9]+)$/,
} as const;

export enum SeoPages {
  Home = 'home',
  Default = 'default',
  Faq = 'faq',
  Installation = 'installation-info',
  Help = 'help',
  Country = 'countryPage',
  Region = 'regionPage',
  GlobalRegion = 'globalRegionPage',
  Destination = 'destination',
  SupportedDevices = 'supportedDevices',
  HowItWorks = 'howItWorks',
  TermsAndConditions = 'termsAndConditions',
  PrivacyPolicy = 'privacyPolicy',
  AffiliateProgram = 'affiliateProgram',
  RewardPoints = 'rewardPoints',
}

/**
 * Strategy map with type-safe function signatures
 * All strategies must conform to SeoStrategyFn type
 */
export const HandlingStrategy = new Map<SeoPages, SeoStrategyFn>([
  [SeoPages.Home, HomeSeoStrategy],
  [SeoPages.Default, DefaultSeoStrategy],
  [SeoPages.Faq, FaqSeoStrategy],
  [SeoPages.Installation, InstallationSeoStrategy],
  [SeoPages.Help, HelpSeoStrategy],
  [SeoPages.Country, CountrySeoStrategy],
  [SeoPages.Region, RegionSeoStrategy],
  [SeoPages.GlobalRegion, GlobalRegionSeoStrategy],
  [SeoPages.Destination, DestinationSeoStrategy],
  [SeoPages.SupportedDevices, SupportedDevicesSeoStrategy],
  [SeoPages.HowItWorks, HowItWorksSeoStrategy],
  [SeoPages.TermsAndConditions, TermsAndConditionsSeoStrategy],
  [SeoPages.PrivacyPolicy, PrivacyPolicySeoStrategy],
  [SeoPages.AffiliateProgram, AffiliateProgramSeoStrategy],
  [SeoPages.RewardPoints, RewardPointsSeoStrategy],
]);

/**
 * Request-scoped cache for SEO content
 * Uses WeakMap for automatic garbage collection when request ends
 * Prevents redundant fetches when canActivateChild runs on nested routes
 */
const REQUEST_SEO_CACHE = new WeakMap<object, Seo>();

/**
 * Tracks if the application has completed initial hydration
 * Used to differentiate between initial page load and client-side navigation
 */
let hasHydrated = false;

/**
 * Tracks the number of navigation events
 * First navigation (0) = initial load, subsequent navigations = client-side routing
 */
let navigationCount = 0;

/**
 * Creates a minimal fallback cache structure when cachedContent is null
 */
function createFallbackCache(): CachedContent {
  return {
    cacheId: '',
    cache: {
      customerId: '',
      config: {
        payments: {
          tap: {
            merchantID: '',
            publishableKey: '',
          },
        },
        gtmId: '',
        currency: 'USD',
        currencyObj: {
          currencyList: [],
          defaultCurrency: {
            currency: 'USD',
            currency_name: 'US Dollar',
          },
        },
        languages: {
          default: 'en',
          supported: [],
        },
        webAppConfig: {
          serverUrl: '',
          name: 'eSIM',
          domainUrl: '',
          s3BucketAssets: '',
          colors: null,
          colorsPreview: null,
          favicon: '',
          logo: '',
          logoPreview: '',
          faviconPreview: '',
        },
        socialLogin: {
          appleClientId: '',
          googleClientId: '',
        },
        planDiscountedPriceEnabled: false,
        contactDetails: {
          address: '',
          emailAddress: '',
          countryCode: '',
          phoneNumber: '',
          social: {
            social: {},
          },
        },
        stripePublishableKey: '',
        universalEsimEnabled: false,
        webSocketUrl: '',
        eKycEnabled: false,
        oneClickInstallAndroidEnabled: false,
        eKycPrivacyPolicyUrl: '',
        eKycTermsUrl: '',
        couponCodesMasterEnabled: false,
        rewardPointsEnabled: false,
        rewardPointsMasterEnabled: false,
        topupPlansEnabled: false,
        topupPlansMasterEnabled: false,
        topupPlansAutoMasterEnabled: false,
        currencyConversionMasterEnabled: false,
        multiLingualMasterEnabled: false,
      },
      singleCountries: [],
      regions: [],
      globalRegions: [],
      imsiIndexes: {},
    },
  };
}

/**
 * Handles server-side SEO application
 */
function handleServerSideSeo(
  seoContentService: SeoContentService,
  strategy: SeoStrategyFn,
  $document: Document,
  nonNullCachedContent: CachedContent,
  page: { page: SeoPages; id?: string }
) {
  const existingContent = REQUEST_SEO_CACHE.get(nonNullCachedContent);

  if (existingContent) {
    try {
      strategy(existingContent, $document, nonNullCachedContent, page.id);
    } catch (error) {
      console.error('SEO Guard [SERVER]: Error applying cached SEO:', error);
    }
    return true;
  }

  return seoContentService.fetchContentInServer().pipe(
    map((seoContent) => {
      if (!seoContent || Object.keys(seoContent).length === 0) {
        console.warn('SEO Guard [SERVER]: SEO content is empty, using local fallback');
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        seoContent = require('./seo_en.json') as Seo;
      }

      REQUEST_SEO_CACHE.set(nonNullCachedContent, seoContent);

      try {
        strategy(seoContent, $document, nonNullCachedContent, page.id);
      } catch (error) {
        console.error('SEO Guard [SERVER]: Error applying SEO strategy:', error);
      }

      return true;
    })
  );
}

/**
 * Gets SEO content with fallback to local template
 */
function getSeoContentWithFallback(seoContentService: SeoContentService): Seo {
  let seoContent = seoContentService.seoContent();
  if (!seoContent || Object.keys(seoContent).length === 0) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    seoContent = require('./seo_en.json');
    console.warn(
      'SEO: Using local fallback template (seo_en.json) - Redis/S3 content not available'
    );
  }
  return seoContent;
}

/**
 * Handles client-side SEO application on NavigationEnd
 */
function handleClientSideNavigation(
  router: Router,
  seoContentService: SeoContentService,
  $document: Document,
  nonNullCachedContent: CachedContent
) {
  router.events.subscribe((event) => {
    if (event instanceof NavigationEnd) {
      const routerState = router.routerState;
      let actualRoute: ActivatedRouteSnapshot = routerState.snapshot.root;

      while (actualRoute.firstChild) {
        actualRoute = actualRoute.firstChild;
      }

      const actualPage = routeMappingWithData(event.url, actualRoute, nonNullCachedContent);
      const actualStrategy = HandlingStrategy.get(actualPage.page) || DefaultSeoStrategy;
      const seoContent = getSeoContentWithFallback(seoContentService);

      try {
        actualStrategy(seoContent, $document, nonNullCachedContent, actualPage.id);
      } catch (error) {
        console.error('SEO Guard [CLIENT]: Error applying SEO strategy:', error);
      }
    }
  });
}

/**
 * Applies SEO immediately if already on target URL
 */
function applySeoIfOnTargetUrl(
  router: Router,
  fullUrl: string,
  seoContentService: SeoContentService,
  strategy: SeoStrategyFn,
  $document: Document,
  nonNullCachedContent: CachedContent,
  page: { page: SeoPages; id?: string }
) {
  const currentUrl = router.url;
  if (currentUrl === fullUrl) {
    const seoContent = getSeoContentWithFallback(seoContentService);
    strategy(seoContent, $document, nonNullCachedContent, page.id);
  }
}

/**
 * Attempts to get cache from warmup service on server
 */
function tryGetCacheFromWarmup(cacheService: CacheService, platform: object): CachedContent | null {
  if (!isPlatformServer(platform)) {
    return null;
  }

  const cacheWarmup = inject(CACHE_WARMUP_SERVICE_TOKEN, { optional: true });
  if (!cacheWarmup) {
    return null;
  }

  try {
    const host = cacheService.getHost();
    const cached = cacheWarmup.getCache(host) as CachedContent | null;
    if (cached) {
      cacheService.cachedContent.set(cached);
      return cached;
    }
  } catch (error) {
    console.warn('SEO Guard [SERVER]: Failed to get cache from warmup service:', error);
  }

  return null;
}

/**
 * Handles client-side browser logic
 */
function handleClientSideBrowser(
  router: Router,
  seoContentService: SeoContentService,
  strategy: SeoStrategyFn,
  $document: Document,
  nonNullCachedContent: CachedContent,
  fullUrl: string,
  page: { page: SeoPages; id?: string }
): void {
  // Increment navigation count on first call
  if (navigationCount === 0) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        navigationCount++;
      }
    });
  }

  // INITIAL HYDRATION: Skip SEO application
  if (!hasHydrated) {
    hasHydrated = true;
    return;
  }

  // CLIENT-SIDE NAVIGATION: Apply SEO updates
  handleClientSideNavigation(router, seoContentService, $document, nonNullCachedContent);

  // Fallback: If NavigationEnd has already fired, process immediately
  applySeoIfOnTargetUrl(
    router,
    fullUrl,
    seoContentService,
    strategy,
    $document,
    nonNullCachedContent,
    page
  );
}

export const seoHandlingGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const platform = inject(PLATFORM_ID);
  const $document = inject(DOCUMENT);
  const cacheService = inject(CacheService);
  let cachedContent: CachedContent | null = cacheService.cachedContent();
  const seoContentService = inject(SeoContentService);

  // Try to get cache from warmup service if null
  cachedContent ??= tryGetCacheFromWarmup(cacheService, platform);

  // If still null, create minimal cache structure for fallback SEO
  if (!cachedContent) {
    console.warn(
      'SEO Guard: cachedContent is null, using minimal cache structure for fallback SEO',
      {
        platform: isPlatformServer(platform) ? 'server' : 'browser',
        url: state.url,
      }
    );
    cachedContent = createFallbackCache();
  }

  const nonNullCachedContent: CachedContent = cachedContent;
  const fullUrl = state.url || '/';
  const page = routeMappingWithData(fullUrl, route, nonNullCachedContent);
  const strategy = HandlingStrategy.get(page.page) || DefaultSeoStrategy;

  // SERVER-SIDE: Get from memory cache
  if (isPlatformServer(platform)) {
    return handleServerSideSeo(seoContentService, strategy, $document, nonNullCachedContent, page);
  }

  // CLIENT-SIDE: Hybrid approach
  if (isPlatformBrowser(platform)) {
    const router = inject(Router);
    handleClientSideBrowser(
      router,
      seoContentService,
      strategy,
      $document,
      nonNullCachedContent,
      fullUrl,
      page
    );
  }

  return true;
};

/**
 * Maps region type to SEO page type
 */
function mapRegionTypeToSeoPage(
  regionType: string,
  destinationId: string
): { page: SeoPages; id: string } | null {
  if (regionType === 'country') {
    return { page: SeoPages.Country, id: destinationId };
  }
  if (regionType === 'region') {
    return { page: SeoPages.Region, id: destinationId };
  }
  if (regionType === 'global') {
    return { page: SeoPages.GlobalRegion, id: destinationId };
  }
  return null;
}

/**
 * Checks route data for destination information
 */
function checkRouteDataForDestination(
  route: ActivatedRouteSnapshot
): { page: SeoPages; id: string } | null {
  if (route.data?.['destination'] && route.data['regionType']) {
    const destination = route.data['destination'];
    const regionType = route.data['regionType'];
    return mapRegionTypeToSeoPage(regionType, destination._id);
  }
  return null;
}

/**
 * Looks up destination in cache by slug
 */
function lookupDestinationInCache(
  destinationSlug: string,
  cachedContent: CachedContent
): { page: SeoPages; id: string } | null {
  const allCountries = cachedContent.cache.singleCountries;
  const allRegions = cachedContent.cache.regions;
  const globalRegions = cachedContent.cache.globalRegions;

  const foundCountry = allCountries.find((country) => country.slug === destinationSlug);
  if (foundCountry) {
    return { page: SeoPages.Country, id: foundCountry._id };
  }

  const foundRegion = allRegions.find((region) => region.slug === destinationSlug);
  if (foundRegion) {
    return { page: SeoPages.Region, id: foundRegion._id };
  }

  const foundGlobalRegion = globalRegions.find(
    (globalRegion) => globalRegion.slug === destinationSlug
  );
  if (foundGlobalRegion) {
    return { page: SeoPages.GlobalRegion, id: foundGlobalRegion._id };
  }

  return null;
}

/**
 * Traverses route tree to find destination data
 */
function traverseRouteTreeForDestination(
  route: ActivatedRouteSnapshot,
  cachedContent?: CachedContent
): { page: SeoPages; id?: string } | null {
  let currentRoute: ActivatedRouteSnapshot | null = route.parent;

  while (currentRoute !== null && currentRoute !== undefined) {
    // Check route.data first
    const routeDataResult = checkRouteDataForDestination(currentRoute);
    if (routeDataResult) {
      return routeDataResult;
    }

    // Check route params for destination slug
    const destinationSlug = currentRoute.params?.['destination'];
    if (destinationSlug && cachedContent) {
      const cacheResult = lookupDestinationInCache(destinationSlug, cachedContent);
      if (cacheResult) {
        return cacheResult;
      }
    }

    currentRoute = currentRoute.parent;
  }

  return null;
}

/**
 * Enhanced route mapping that checks both URL patterns and route data
 * Prioritizes route.data set by destinationHandlerGuard for slug-based routes
 * Traverses route tree to find destination data on parent routes
 * Falls back to cache lookup if route data isn't available
 */
export function routeMappingWithData(
  url: string,
  route: ActivatedRouteSnapshot,
  cachedContent?: CachedContent
): { page: SeoPages; id?: string } {
  // Strategy 1: Check current route data
  const currentRouteResult = checkRouteDataForDestination(route);
  if (currentRouteResult) {
    return currentRouteResult;
  }

  // Strategy 2: Traverse up the route tree
  const treeResult = traverseRouteTreeForDestination(route, cachedContent);
  if (treeResult) {
    return treeResult;
  }

  // Strategy 3: Fallback to URL-based mapping
  return routeMapping(url);
}

/**
 * Maps URL patterns to SEO page types
 * Used for static routes and backwards compatibility with ID-based routes
 */
export function routeMapping(url: string): { page: SeoPages; id?: string } {
  // Home page
  if (url === ROUTES.HOME || url === '' || url.length === 0) {
    return { page: SeoPages.Home };
  }

  // FAQ page
  if (url === ROUTES.FAQ) {
    return { page: SeoPages.Faq };
  }

  // Installation page
  if (url.startsWith(ROUTES.INSTALLATION)) {
    return { page: SeoPages.Installation };
  }

  // Contact/Help page
  if (url === ROUTES.CONTACT) {
    return { page: SeoPages.Help };
  }

  // Destination/Buy Plans page
  if (url === ROUTES.DESTINATION || url === ROUTES.BUY_PLAN_DESTINATION) {
    return { page: SeoPages.Destination };
  }

  // Supported Devices page
  if (url === ROUTES.SUPPORTED_DEVICES || url === ROUTES.SUPPORTED_DEVICES_ALT) {
    return { page: SeoPages.SupportedDevices };
  }

  // How It Works page
  if (url === ROUTES.HOW_IT_WORKS) {
    return { page: SeoPages.HowItWorks };
  }

  // Terms and Conditions page
  if (url === ROUTES.TERMS) {
    return { page: SeoPages.TermsAndConditions };
  }

  // Privacy Policy page
  if (url === ROUTES.PRIVACY) {
    return { page: SeoPages.PrivacyPolicy };
  }

  // Affiliate Program page
  if (url === ROUTES.AFFILIATE) {
    return { page: SeoPages.AffiliateProgram };
  }

  // Reward Points page
  if (url === ROUTES.REWARDS) {
    return { page: SeoPages.RewardPoints };
  }

  // Handle country pages: /buy-plan/plans/country/{countryId}/{planId}
  const countryMatch = ROUTE_PATTERNS.COUNTRY.exec(url);
  if (countryMatch) {
    return { page: SeoPages.Country, id: countryMatch[1] };
  }

  // Handle region pages: /buy-plan/plans/region/{regionId}/{planId}
  const regionMatch = ROUTE_PATTERNS.REGION.exec(url);
  if (regionMatch) {
    return { page: SeoPages.Region, id: regionMatch[1] };
  }

  // Handle global region pages: /buy-plan/plans/global/{globalRegionId}
  const globalMatch = ROUTE_PATTERNS.GLOBAL.exec(url);
  if (globalMatch) {
    return { page: SeoPages.GlobalRegion, id: globalMatch[1] };
  }

  // Default fallback
  return { page: SeoPages.Default };
}
